/**
 * What a guest has typed into checkout, kept in this browser so a reload does
 * not start them from scratch -- the same thing the sell page does for a
 * listing draft (ListingProvider, `postData`).
 *
 * Email, name and address carry across listings: they describe the buyer, not
 * the item. The shipping pick is tied to its listing, because rates are quoted
 * per parcel; it is matched back by carrier and service once the new quote
 * arrives, never by position.
 *
 * Kept 30 days, and cleared when the guest's purchase goes through. Every read
 * and write survives blocked storage (private windows, locked-down browsers):
 * losing the draft is fine, breaking checkout is not.
 */
import { z } from 'zod';
import { LS_Delete, LS_Get, LS_Set } from './local-storage';

export const GUEST_CHECKOUT_DRAFT_KEY = 'guestCheckoutDraft';

/** How long a draft is offered back. */
export const GUEST_CHECKOUT_DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const field = z.string().max(200);

export const GuestCheckoutDraftSchema = z.object({
  email: z.string().max(254).optional(),
  address: z
    .object({
      name: field,
      line1: field,
      line2: field,
      city: field,
      state: field,
      postal_code: field,
      country: field,
    })
    .optional(),
  shipping: z
    .object({
      listingId: z.string(),
      provider: z.string(),
      service: z.string(),
    })
    .optional(),
  savedAt: z.number(),
});

export type GuestCheckoutDraft = z.infer<typeof GuestCheckoutDraftSchema>;
export type GuestCheckoutDraftAddress = NonNullable<GuestCheckoutDraft['address']>;

/** The saved draft, or null when there is none, it is stale, or it is junk. */
export function readGuestCheckoutDraft(now: number = Date.now()): GuestCheckoutDraft | null {
  try {
    const parsed = GuestCheckoutDraftSchema.safeParse(LS_Get(GUEST_CHECKOUT_DRAFT_KEY));
    if (!parsed.success) return null;
    if (now - parsed.data.savedAt > GUEST_CHECKOUT_DRAFT_MAX_AGE_MS) return null;
    return parsed.data;
    // eslint-disable-next-line sellwild-code/catch-must-log -- blocked storage is a browser setting, not a fault; logging it would file one error per private-window page view
  } catch {
    // Storage is blocked. No draft is the right answer, not an error.
    return null;
  }
}

/** Merge these fields into the saved draft. */
export function saveGuestCheckoutDraft(
  patch: Omit<Partial<GuestCheckoutDraft>, 'savedAt'>,
  now: number = Date.now(),
): void {
  try {
    const current = readGuestCheckoutDraft(now);
    LS_Set(GUEST_CHECKOUT_DRAFT_KEY, { ...current, ...patch, savedAt: now });
    // eslint-disable-next-line sellwild-code/catch-must-log -- blocked or full storage, as above: expected, and costs only the convenience
  } catch {
    // Storage is blocked or full. The buyer keeps typing; they just lose the
    // convenience on reload.
  }
}

export function clearGuestCheckoutDraft(): void {
  try {
    LS_Delete(GUEST_CHECKOUT_DRAFT_KEY);
    // eslint-disable-next-line sellwild-code/catch-must-log -- blocked storage, as above
  } catch {
    // Blocked storage has nothing to clear.
  }
}

/**
 * Which of freshly quoted rates the guest had picked, or 0 (the cheapest
 * first, as checkout already defaults to) when the pick was for another
 * listing or is no longer offered.
 */
export function pickSavedRateIndex(
  rates: ReadonlyArray<{ service: string; provider: { name: string } }>,
  saved: GuestCheckoutDraft['shipping'],
  listingId: string | number | undefined,
): number {
  if (!saved || listingId === undefined || saved.listingId !== String(listingId)) return 0;
  const index = rates.findIndex(
    (rate) => rate.provider.name === saved.provider && rate.service === saved.service,
  );
  return index >= 0 ? index : 0;
}
